import { useState } from "react"
import { Link, useParams } from "react-router-dom"
import { ArrowLeft, Plus, Tags as TagsIcon, Trash2 } from "lucide-react"
import { api } from "../api/client"
import { useApp } from "../state/AppState"
import { useProject } from "../state/useProject"
import { ACCENT_KEYS, accent, accentFor } from "../design/palette"
import {
	Button,
	Card,
	ColourPicker,
	EmptyState,
	Field,
	Input,
	Modal,
	SectionHeading,
	Spinner,
	cx
} from "../components/ui"
import { SplitBar } from "../components/charts/StatTile"
import { plural } from "../lib/format"
import { NotFoundPage } from "./NotFoundPage"

export function TagsPage() {
	const { projectId } = useParams()
	const { project, tags, loading, error, reload } = useProject(projectId)
	const { toast } = useApp()

	const [name, setName] = useState("")
	const [colour, setColour] = useState("teal")
	const [saving, setSaving] = useState(false)
	const [formError, setFormError] = useState(null)
	const [pendingDelete, setPendingDelete] = useState(null)

	async function createTag(event) {
		event.preventDefault()
		if (!name.trim()) return
		setSaving(true)
		setFormError(null)
		try {
			await api.createTag(projectId, { name: name.trim(), colour })
			setName("")
			setColour(accentFor(Math.random().toString()))
			await reload({ quiet: true })
			toast("Tag created", "success")
		} catch (cause) {
			setFormError(cause.message)
		} finally {
			setSaving(false)
		}
	}

	async function renameTag(tag, nextName) {
		if (!nextName.trim() || nextName === tag.name) return
		try {
			await api.updateTag(projectId, tag.tag_id, { name: nextName.trim() })
			await reload({ quiet: true })
		} catch (cause) {
			toast(cause.message, "error")
			await reload({ quiet: true })
		}
	}

	async function recolourTag(tag, nextColour) {
		try {
			await api.updateTag(projectId, tag.tag_id, { colour: nextColour })
			await reload({ quiet: true })
		} catch (cause) {
			toast(cause.message, "error")
		}
	}

	async function confirmDelete() {
		const tag = pendingDelete
		setPendingDelete(null)
		try {
			await api.deleteTag(projectId, tag.tag_id)
			await reload({ quiet: true })
			toast(`Deleted "${tag.name}"`, "success")
		} catch (cause) {
			toast(cause.message, "error")
		}
	}

	if (error?.status === 404) return <NotFoundPage />
	if (loading && !project) return <Spinner label="Loading tags" />
	if (!project) return null

	// Creating, renaming, recolouring and deleting tags are the owner's; anyone
	// else sees the list without controls the server would refuse.
	const isOwner = Boolean(project.is_owner)

	return (
		<div className="space-y-5">
			<div>
				<Link
					to={`/projects/${projectId}`}
					className="inline-flex items-center gap-1.5 text-[0.8125rem] font-medium text-ink-500 transition-colors hover:text-ink-900"
				>
					<ArrowLeft size={14} aria-hidden />
					{project.name}
				</Link>
				<h1 className="mt-2 text-xl font-semibold tracking-tight text-ink-900">
					Tags
				</h1>
				<p className="mt-0.5 max-w-2xl text-[0.8125rem] leading-relaxed text-ink-500">
					Tags are how a long thread stays navigable - group by discipline, by
					who owns the decision, or by whatever the meeting keeps coming back to.
					They are scoped to this project.
				</p>
			</div>

			{isOwner ? (
				<Card className="p-4">
					<form onSubmit={createTag} className="flex flex-wrap items-end gap-3">
						<Field label="New tag" error={formError} className="min-w-[14rem] flex-1">
							<Input
								value={name}
								onChange={(event) => setName(event.target.value)}
								placeholder="Client decision"
								maxLength={40}
							/>
						</Field>
						<div>
							<span className="mb-1.5 block text-[0.8125rem] font-medium text-ink-700">
								Colour
							</span>
							<ColourPicker value={colour} onChange={setColour} options={ACCENT_KEYS} />
						</div>
						<Button
							type="submit"
							variant="primary"
							icon={Plus}
							busy={saving}
							disabled={saving || !name.trim()}
						>
							Add tag
						</Button>
					</form>
				</Card>
			) : (
				<p className="rounded-lg bg-ink-100/70 px-3.5 py-2.5 text-[0.8125rem] leading-relaxed text-ink-600">
					Only the project owner can add, rename or delete tags. You can still put
					any of these on an issue.
				</p>
			)}

			<div>
				<SectionHeading
					title={`${plural(tags.length, "tag")} in this project`}
					description="Deleting a tag removes it from every issue - the issues themselves stay."
					className="mb-3"
				/>

				{tags.length === 0 ? (
					<EmptyState
						icon={TagsIcon}
						title="No tags yet"
						description={
							isOwner
								? "Add the first one above. Two or three broad categories work better than a dozen narrow ones."
								: "The project owner has not added any yet."
						}
					/>
				) : (
					<Card className="divide-y divide-ink-100">
						{tags.map((tag) => (
							<TagRow
								key={tag.tag_id}
								tag={tag}
								canManage={isOwner}
								onRename={(next) => renameTag(tag, next)}
								onRecolour={(next) => recolourTag(tag, next)}
								onDelete={() => setPendingDelete(tag)}
							/>
						))}
					</Card>
				)}
			</div>

			<Modal
				open={Boolean(pendingDelete)}
				onClose={() => setPendingDelete(null)}
				title={`Delete "${pendingDelete?.name}"?`}
				size="sm"
				footer={
					<>
						<Button onClick={() => setPendingDelete(null)}>Cancel</Button>
						<Button variant="danger" icon={Trash2} onClick={confirmDelete}>
							Delete tag
						</Button>
					</>
				}
			>
				<p className="text-[0.8125rem] leading-relaxed text-ink-600">
					It will be removed from{" "}
					<strong className="font-semibold text-ink-900">
						{plural(pendingDelete?.issue_count || 0, "issue")}
					</strong>
					. The issues and their conversations are untouched, and this cannot be
					undone.
				</p>
			</Modal>
		</div>
	)
}

function TagRow({ tag, canManage, onRename, onRecolour, onDelete }) {
	const [draft, setDraft] = useState(tag.name)
	const [editing, setEditing] = useState(false)
	const [pickerOpen, setPickerOpen] = useState(false)
	const tone = accent(tag.colour)

	return (
		<div className="flex flex-wrap items-center gap-3 px-3.5 py-3">
			{canManage ? (
				<button
					type="button"
					onClick={() => setPickerOpen((v) => !v)}
					aria-label={`Change colour of ${tag.name}`}
					className={cx(
						"h-6 w-6 shrink-0 rounded-full ring-1 ring-black/10 transition hover:scale-110",
						tone.swatch
					)}
				/>
			) : (
				<span
					className={cx("h-6 w-6 shrink-0 rounded-full ring-1 ring-black/10", tone.swatch)}
					aria-hidden
				/>
			)}

			<div className="min-w-[10rem] flex-1">
				{!canManage ? (
					<span className="text-[0.9375rem] font-medium text-ink-900">{tag.name}</span>
				) : editing ? (
					<Input
						value={draft}
						autoFocus
						maxLength={40}
						onChange={(event) => setDraft(event.target.value)}
						onBlur={() => {
							setEditing(false)
							onRename(draft)
						}}
						onKeyDown={(event) => {
							if (event.key === "Enter") event.currentTarget.blur()
							if (event.key === "Escape") {
								setDraft(tag.name)
								setEditing(false)
							}
						}}
						className="h-8"
					/>
				) : (
					<button
						type="button"
						onClick={() => setEditing(true)}
						className="text-left text-[0.9375rem] font-medium text-ink-900 hover:text-brand-800"
						title="Click to rename"
					>
						{tag.name}
					</button>
				)}
			</div>

			<div className="w-40">
				<SplitBar open={tag.open_count} closed={tag.issue_count - tag.open_count} />
				<p className="mt-1 text-2xs text-ink-500">
					{tag.issue_count === 0
						? "Not used yet"
						: `${tag.open_count} open of ${tag.issue_count}`}
				</p>
			</div>

			{canManage && (
				<Button size="sm" variant="ghost" icon={Trash2} onClick={onDelete}>
					Delete
				</Button>
			)}

			{canManage && pickerOpen && (
				<div className="w-full pt-1">
					<ColourPicker
						value={tag.colour}
						onChange={(next) => {
							onRecolour(next)
							setPickerOpen(false)
						}}
						options={ACCENT_KEYS}
					/>
				</div>
			)}
		</div>
	)
}
